import type { ParsedUrlQuery } from 'querystring';

import {
    BlockList,
    getPost,
    initializeApollo,
    initializeMFEs,
    PlacementProvider,
} from '@zillow/cms-blocks';
import { Box } from '@zillow/constellation';
import {
    GetStaticPaths,
    GetStaticProps,
    GetServerSideProps,
    GetStaticPropsResult,
} from 'next';
import React from 'react';

import { REVALIDATION_INTERVAL } from '../../server/config';
import { Page, Post } from '../types/shared';

import { processBlocksData } from './blocks';
import { shouldUseVisualRefreshTheme } from './document';
import { withD } from './withDynamicThing';
// Define types for the component props and inputs
export type PostPageContentProps = {
    post: (Post | Page) & {
        menu?: unknown; // This comes from settings for posts, or directly from page
    };
    preview?: boolean;
    useVisualRefreshTheme?: boolean;
    knotchAccountId?: string;
    contentType?: 'B2B' | 'B2C';
};

// Define the minimal props for LayoutComponent
export type LayoutComponentProps = {
    children: React.ReactNode;
    head: string;
    menu?: unknown;
    preview?: boolean;
    submenu?: unknown;
    header?: { disabled?: boolean; transparent?: boolean };
    NavComponent?: React.ComponentType<unknown>;
    [key: string]: unknown;
};

type BuildPrimaryPostPageInputs = {
    site:
        | 'ZPA'
        | 'LEARN'
        | 'ZHL'
        | 'RENTALS'
        | 'ROOT'
        | 'CAREERS'
        | 'CONSTRUCTION'
        | 'BRANDS'
        | 'AGENTS'
        | 'PREFERRED';
    basePath?: string;
    // Component to render the layout
    LayoutComponent: React.ComponentType<LayoutComponentProps>;
    // Content component to render posts and pages
    ContentComponent?: React.ComponentType<{
        post: Post | Page;
        preview?: boolean;
        knotchAccountId?: string;
        contentType?: 'B2B' | 'B2C';
        hasStickyNav?: boolean;
    }>;
    // For special handling of page paths (e.g., prepending paths)
    pathFormatter?: (slug: string[]) => string;
    // For handling additional layout props
    additionalLayoutProps?: Record<string, unknown>;
    // Post type to fetch (default is undefined, which fetches both)
    postType?: ('POST' | 'PAGE')[];
    // Skip applying withD to the Page component
    skipWithD?: boolean;
    // Whether to use the base path for the post ID
    useBasePathForId?: boolean;
    // Knotch account ID
    knotchAccountId?: string;
    // Content type
    contentType?: 'B2B' | 'B2C';
    // Specify which prop name to use for menu data ('menu' | 'submenu')
    menuPropName?: 'menu' | 'submenu';
    hasStickyNav?: boolean;
};

// Default content component that renders blocks and footer
const DefaultContentComponent: React.FC<{
    post: PostPageContentProps['post'];
}> = ({ post }) => (
    <>
        <PlacementProvider placement={post.type === 'post' ? 'post' : 'page'}>
            <BlockList blocks={post?.blocks || []} />
        </PlacementProvider>
        {post?.footer && (
            <Box
                sx={{
                    margin: '0 auto',
                    padding: '0 $spacing.sm',
                    maxWidth: '$breakpoints.xxl',
                }}
            >
                <BlockList blocks={post?.footer?.blocks || []} />
            </Box>
        )}
    </>
);

// Shared logic for both static and server-side props
async function fetchPostPageProps({
    params,
    inputs,
    isStatic = false,
}: {
    params: ParsedUrlQuery | undefined;
    inputs: BuildPrimaryPostPageInputs;
    isStatic?: boolean;
}): Promise<GetStaticPropsResult<PostPageContentProps>> {
    // If no slug is provided, default to / to grab the front page
    const { slug = ['/'] } = params || {};
    // Ensure slug is an array
    const slugArray = Array.isArray(slug) ? slug : [slug];

    initializeApollo();
    // Refresh MFE's to pick up changes on cache expiry
    initializeMFEs();

    let id = slugArray.join('/');
    if (inputs.useBasePathForId) {
        // Strip leading and trailing slashes from basePath
        const sanitizedBasePath = (inputs.basePath || '').replace(
            /^\/+|\/+$/g,
            ''
        );

        id =
            sanitizedBasePath +
            (slugArray[0] === '/' ? '' : '/') +
            slugArray.join('/');
    }

    const postVariables = {
        site: inputs.site,
        id,
        idType: 'URI',
        withSettings: true,
    };
    // Add postType if specified
    if (inputs.postType) {
        postVariables['postType'] = inputs.postType;
    }

    const { post, settings } = await getPost({
        variables: postVariables,
        fetchPolicy: 'no-cache',
    });

    // Check for post status.
    if (post?.status !== 'publish') {
        return {
            notFound: true,
        };
    }

    const blocks = await processBlocksData(post);
    const footerBlocks = await processBlocksData(post?.footer);
    const sidebarBlocks = await processBlocksData(post?.sidebar);

    const menu = post?.type === 'post' ? settings?.menu : post?.menu;

    const props: PostPageContentProps = {
        post: {
            ...post,
            blocks,
            footer:
                footerBlocks.length > 0
                    ? { ...post.footer, blocks: footerBlocks }
                    : null,
            sidebar:
                sidebarBlocks.length > 0
                    ? { ...post.sidebar, blocks: sidebarBlocks }
                    : null,
            menu: menu || null,
        },
    };

    // Add visual refresh theme if needed and formatter is provided
    if (inputs.basePath && typeof shouldUseVisualRefreshTheme === 'function') {
        const path = inputs.pathFormatter
            ? inputs.pathFormatter(slugArray)
            : inputs.basePath +
              (slugArray[0] === '/' ? '/' : '/' + slugArray.join('/') + '/');
        props.useVisualRefreshTheme = shouldUseVisualRefreshTheme(path);
    } else if (
        inputs.pathFormatter &&
        typeof shouldUseVisualRefreshTheme === 'function'
    ) {
        // Use the pathFormatter for root pages without basePath
        const formattedPath = inputs.pathFormatter(slugArray);
        props.useVisualRefreshTheme =
            shouldUseVisualRefreshTheme(formattedPath);
    }

    if (isStatic) {
        return {
            props,
            revalidate: REVALIDATION_INTERVAL,
        };
    }

    return {
        props,
    };
}

export async function buildPrimaryPostPage(
    inputs: BuildPrimaryPostPageInputs
): Promise<{
    getStaticProps: GetStaticProps<PostPageContentProps>;
    getServerSideProps: GetServerSideProps<PostPageContentProps>;
    getStaticPaths: GetStaticPaths;
    Page: React.ComponentType<PostPageContentProps>;
    WrappedPage: React.ComponentType<PostPageContentProps>;
}> {
    const getStaticProps: GetStaticProps<PostPageContentProps> = async (
        context
    ) => {
        return fetchPostPageProps({
            params: context.params,
            inputs,
            isStatic: true,
        });
    };

    const getServerSideProps: GetServerSideProps<PostPageContentProps> = async (
        context
    ) => {
        return fetchPostPageProps({ params: context.params, inputs });
    };

    const getStaticPaths: GetStaticPaths = async () => {
        return {
            paths: [],
            fallback: 'blocking',
        };
    };

    const PageComponent: React.FC<PostPageContentProps> = ({
        post,
        preview,
        // useVisualRefreshTheme is passed to the component but not used directly
    }) => {
        const ContentComponent =
            inputs.ContentComponent || DefaultContentComponent;

        // Determine layout props based on site and component
        const layoutProps: LayoutComponentProps = {
            head: post.head || '',
            children: (
                <ContentComponent
                    post={post}
                    preview={preview}
                    knotchAccountId={inputs.knotchAccountId}
                    contentType={inputs.contentType}
                    hasStickyNav={inputs.hasStickyNav}
                />
            ),
            preview,
        };

        // Handle ZPA-style layout
        if (post.header) {
            layoutProps.header = post.header;
        }
        // Add menu props based on layout type
        if (post?.menu) {
            const propName = inputs.menuPropName || 'menu';
            layoutProps[propName] = post.menu;
        }

        // Add any additional props
        if (inputs.additionalLayoutProps) {
            Object.assign(layoutProps, inputs.additionalLayoutProps);
        }

        return <inputs.LayoutComponent {...layoutProps} />;
    };

    // Apply withD to the Page component, unless skipWithD is specified
    const WrappedPage = inputs.skipWithD
        ? PageComponent
        : await withD(PageComponent);

    return {
        getStaticProps,
        getServerSideProps,
        getStaticPaths,
        Page: PageComponent,
        WrappedPage,
    };
}
